// web/src/player/lipSync.ts
import type { Mode } from "./types";

export const MOUTH_RANGE = {
  roast: { closed: 0, maxOpen: -90 },  // roast opens with NEGATIVE values
  toast: { closed: 3, maxOpen: 50 },   // toast: closed 3, open 50 (from the old web / iOS Mr Toast)
} as const;

/** Extra per-mode low-pass (EMA) on the FINAL mouth value: 0 = none, →1 = heavier
 *  smoothing. Toast's artboard reacts dramatically to the Mouth value so the raw
 *  signal reads as "too sensitive"; a light EMA smooths the movement. Roast is 0
 *  (the return value is then byte-identical to before — no roast regression). Dial
 *  toast up for smoother / down for snappier. */
export const MOUTH_SMOOTHING: Record<Mode, number> = {
  roast: 0,
  toast: 0.6,
};

export const LIP = {
  FFT_SIZE: 1024,
  SILENCE_THRESHOLD: 0.03,
  MIN_THRESHOLD: 0.02,
  POWER_CURVE: 0.65,
  ATTACK: 0.1,
  RELEASE: 0.45,
  DECAY_ACCEL: 1.5,
  PEAK_DECAY: 0.995,
  PEAK_FLOOR: 0.02,
} as const;

export class LipSyncAnalyser {
  private ctx: AudioContext;
  private analyser: AnalyserNode;
  private duckGain: GainNode;
  private buf: Float32Array<ArrayBuffer>;
  private runningPeak: number = LIP.PEAK_FLOOR;
  private _speaking = false;
  private prevMouth = 0;
  private history: number[] = [];
  private readonly historySize = 2;
  private silentFrames = 0;
  private prevSmoothed = 0; // EMA state for the per-mode MOUTH_SMOOTHING low-pass

  constructor(audioEl: HTMLAudioElement) {
    this.ctx = new AudioContext();
    const source = this.ctx.createMediaElementSource(audioEl);
    this.duckGain = this.ctx.createGain();
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = LIP.FFT_SIZE;
    this.analyser.smoothingTimeConstant = 0;
    source.connect(this.duckGain);
    this.duckGain.connect(this.analyser);
    this.analyser.connect(this.ctx.destination);
    this.buf = new Float32Array(this.analyser.fftSize) as Float32Array<ArrayBuffer>;
  }

  get context(): AudioContext { return this.ctx; }
  /** Whether the most recently analysed frame contained audible sound (above the
   *  silence threshold). Used to gate caption highlighting to actual playback. */
  get isSpeaking(): boolean { return this._speaking; }
  /** ADDITIVE tap for video export: parallel-connect the voice terminal (analyser)
   *  to an extra destination (a MediaStreamAudioDestinationNode) WITHOUT touching the
   *  existing analyser → ctx.destination wiring. Speaker output stays byte-identical;
   *  the tap just receives a copy of the voice for the capture mix. */
  connectTap(node: AudioNode) { this.analyser.connect(node); }
  resume() { if (this.ctx.state === "suspended") this.ctx.resume(); }
  setDuck(gain: number) { this.duckGain.gain.value = gain; }
  dispose() { this.ctx.close().catch(() => {}); }

  private rms(): number {
    this.analyser.getFloatTimeDomainData(this.buf);
    let sum = 0;
    for (let i = 0; i < this.buf.length; i++) sum += this.buf[i] * this.buf[i];
    return Math.sqrt(sum / this.buf.length);
  }

  mouthValue(mode: Mode): number {
    const range = MOUTH_RANGE[mode];
    let amp = this.rms();
    this.runningPeak = Math.max(this.runningPeak * LIP.PEAK_DECAY, amp, LIP.PEAK_FLOOR);
    amp = Math.min(1, amp / this.runningPeak);
    this._speaking = amp >= LIP.SILENCE_THRESHOLD;

    if (amp < LIP.SILENCE_THRESHOLD) {
      if (++this.silentFrames >= 2) { this.prevMouth = 0; this.history = []; this.prevSmoothed = range.closed; return range.closed; }
    } else this.silentFrames = 0;

    const thr = amp < LIP.MIN_THRESHOLD ? 0 : amp;
    const openness = this.smooth(this.accelerate(Math.pow(thr, LIP.POWER_CURVE)));
    const target = range.closed + openness * (range.maxOpen - range.closed);
    // Per-mode EMA low-pass. roast k=0 → returns target unchanged (no regression);
    // toast k>0 → smooths the movement.
    const k = MOUTH_SMOOTHING[mode];
    this.prevSmoothed = this.prevSmoothed * k + target * (1 - k);
    return Math.round(this.prevSmoothed);
  }

  private accelerate(raw: number): number {
    if (raw < this.prevMouthN) {
      const a = this.prevMouthN + (raw - this.prevMouthN) * LIP.DECAY_ACCEL;
      return a < 0 ? 0 : a;
    }
    return raw;
  }
  private get prevMouthN() { return Math.abs(this.prevMouth); }

  private smooth(raw: number): number {
    this.history.push(raw);
    if (this.history.length > this.historySize) this.history.shift();
    const avg = this.history.reduce((a, b) => a + b, 0) / this.history.length;
    const opening = raw > this.prevMouth;
    const nearlyClosed = this.prevMouth < 0.13 && raw < 0.13;
    let f = nearlyClosed ? 0.05 : opening ? LIP.ATTACK : LIP.RELEASE;
    if (opening && this.prevMouth < 0.17 && raw > 0.26) f = 0;
    const s = this.prevMouth * f + avg * (1 - f);
    this.prevMouth = s;
    return s;
  }
}
